import * as Sentry from '@sentry/browser'
import { useDispatch, useSelector } from 'react-redux'
import { useShareMyMemberCouponsMutation } from 'graphQL/dinein/generate/operations'
import useModal from 'hooks/useModal'
import { useTranslation } from 'react-i18next'
import { buffetSelector, setAlertLabelBox, setAllowedSharingCoupon } from 'store/slices/buffetSlice'
import { MAXIMUM_SHARE_COUPONS } from 'variables/dineIn'
import { dineInClient } from 'variables/services'
import { useHistory } from 'react-router-dom'
import { SubPageName } from 'enums/buffet'
import { AlertLabelBoxProps } from 'components/base-ui/AlertLabelBox'
import { isDevelopment } from 'variables/environment'

const useCombineCoupon = () => {
  const buffet = useSelector(buffetSelector)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const modalLimitSharingCoupons = useModal()
  const modalCombineCoupons = useModal()

  const handleClickCombineCoupon = () => {
    if (buffet.totalUsersSharedCoupons === MAXIMUM_SHARE_COUPONS) {
      modalLimitSharingCoupons.setOpen(true)
      return
    } else if (!buffet.allowedSharingCoupon) {
      modalCombineCoupons.setOpen(true)
      return
    }

    history.push(SubPageName.InviteFriends)
  }

  const toggleAlertLabel = (alertText: string, type: AlertLabelBoxProps['type'] = 'success') => {
    dispatch(setAlertLabelBox({ show: true, alertText, type }))
    setTimeout(() => dispatch(setAlertLabelBox({ show: false, alertText: '', type })), 3500)
  }

  const [shareMyMemberCoupons] = useShareMyMemberCouponsMutation({
    client: dineInClient,
    onCompleted: ({ shareMyMemberCoupons: { orderInvalids } }) => {
      if (orderInvalids.length === 0) {
        modalCombineCoupons.reset()
        dispatch(setAllowedSharingCoupon(true))
        setTimeout(() => {
          toggleAlertLabel(t('dineIn.invitePage.shareCouponSuccess'))
          history.push(SubPageName.InviteFriends)
        }, 300) // wait for animation closing modal
      }
    },
    onError: (error) => {
      modalCombineCoupons.reset()
      !isDevelopment &&
        Sentry.captureException(new Error(`Buffet: Can't confirm share coupon: ${JSON.stringify(error)}`))
    },
  })

  const confirmCombineCoupon = () => {
    modalCombineCoupons.setProcessing(true)
    shareMyMemberCoupons({
      variables: {
        orderID: buffet.table.orderID,
      },
    })
  }

  return {
    handleClickCombineCoupon,
    confirmCombineCoupon,
    modalLimitSharingCoupons,
    modalCombineCoupons,
    toggleAlertLabel,
  }
}

export default useCombineCoupon
